<template>
    <BaseCard padding="lg" class="flex flex-col gap-2.5">
        <p v-if="headline" class="camp-headline">{{ headline }}</p>

        <div v-if="hasChart" class="camp-innerbox flex flex-col gap-6">
            <CampSegmentedBar v-for="row in chartRows" :key="row.key" :label="row.label" :distribution="row.distribution" :avg="row.avg" />
            <CampLegend />
        </div>

        <div v-if="insight?.strengths?.length" class="camp-sub flex flex-col gap-2.5">
            <div class="camp-sub__head flex items-center gap-2 body-small-semibold" style="color: #cd2f7b">
                <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 shrink-0" aria-hidden="true">
                    <circle cx="8" cy="8" r="8" fill="#cd2f7b" />
                    <path d="M8 11.5V4.8 M5.2 7.6 L8 4.6 L10.8 7.6" stroke="#fff" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                Strengths
            </div>
            <hr class="border-t border-valence-cream-300 m-0" />
            <div v-for="(entry, i) in insight.strengths" :key="`s-${i}`" class="flex flex-col gap-1">
                <p class="body-small-semibold text-valence-stone-900 m-0">{{ entry.label }}</p>
                <ul class="pl-5 m-0">
                    <li class="body-small text-valence-stone-900 list-disc">{{ entry.detail }}</li>
                </ul>
            </div>
        </div>

        <div v-if="insight?.opportunities?.length" class="camp-sub flex flex-col gap-2.5">
            <div class="camp-sub__head flex items-center gap-2 body-small-semibold" style="color: #7e78de">
                <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 shrink-0" aria-hidden="true">
                    <circle cx="8" cy="8" r="8" fill="#7e78de" />
                    <path d="M5.2 10.8 L10.8 5.2 M6.4 5.2 L10.8 5.2 L10.8 9.6" stroke="#fff" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                Opportunities
            </div>
            <hr class="border-t border-valence-cream-300 m-0" />
            <div v-for="(entry, i) in insight.opportunities" :key="`o-${i}`" class="flex flex-col gap-1">
                <p class="body-small-semibold text-valence-stone-900 m-0">{{ entry.label }}</p>
                <ul class="pl-5 m-0">
                    <li class="body-small text-valence-stone-900 list-disc">{{ entry.detail }}</li>
                </ul>
            </div>
        </div>

        <div v-if="insight?.summary" class="flex items-stretch gap-2.5 p-2.5">
            <span class="w-1 rounded-full shrink-0" style="background: #cd2f7b" aria-hidden="true"></span>
            <p class="body-small text-valence-stone-900 m-0">{{ insight.summary }}</p>
        </div>
    </BaseCard>
</template>

<script setup lang="ts">
import BaseCard from "~vue/components/BaseCard.vue";
import CampLegend from "~vue/pages/reflections/CampLegend.vue";
import CampSegmentedBar from "~vue/pages/reflections/CampSegmentedBar.vue";
import { computed } from "vue";

interface ChartRow {
    key: string;
    label: string;
    distribution: number[];
    avg: number | null;
}

interface InsightEntry {
    label: string;
    detail: string;
}

interface Insight {
    strengths?: InsightEntry[];
    opportunities?: InsightEntry[];
    summary?: string;
}

interface Props {
    headline?: string;
    chartRows?: ChartRow[];
    insight?: Insight | null;
}

const props = defineProps<Props>();

const hasChart = computed(() => (props.chartRows?.length ?? 0) > 0);
</script>

<style scoped>
.camp-headline {
    font-family: var(--font-serif);
    font-weight: 500;
    font-size: 20px;
    line-height: 24px;
    color: #000;
    margin: 0;
}

.camp-innerbox {
    border: 1px solid var(--color-valence-cream-300, #e7e2db);
    border-radius: 10px;
    padding: 20px;
}

.camp-sub {
    background: #fff;
    border: 1px solid var(--color-valence-cream-300, #e7e2db);
    border-radius: 10px;
    padding: 20px;
}
</style>
